import {
  Block,
  BlockTitle,
  Button,
  Icon,
  List,
  ListInput,
  ListItem,
  Page,
  Popup,
  Preloader,
  Progressbar,
} from "framework7-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PopupNavbar from "../../components/popup-navbar";
import { usePopupBackClose } from "../../hooks/use-back-close";
import { useEnterKeys } from "../../hooks/use-enter-keys";
import { vibrate } from "../../hooks/use-haptics";
import { useToast } from "../../hooks/use-toast";
import {
  addParticipants,
  assignMe,
  createGroup,
  deleteParticipant,
  getGroupStatus,
  refreshGroups,
  selectCurrentGroup,
  updateGroup,
} from "../../redux/reducer/app-slice";
import { confirmDialog } from "../../utils/confirm-dialog";
import { queuedText } from "../../utils/outbox";
import {
  duplicateNameMessage,
  isDuplicateName,
} from "../../utils/participant-names";
import { participantIdsInUse } from "../../utils/participants-in-use";
import AssignStep from "./assign-step";

const noop = () => {};

const TITLES = {
  create: ["Gruppe erstellen", "Teilnehmer hinzufügen", "Wer bist du?"],
  edit: ["Gruppe bearbeiten", "Teilnehmer"],
};

const CreateGroupPopup = ({
  opened,
  onClose = noop,
  onRenameParticipant = noop,
  isCurrentGroup = false,
  startAtParticipants = false,
}) => {
  // Kept after a failed participant save, so a retry creates no second group.
  const [savedGroup, setSavedGroup] = useState(null);
  const [name, setName] = useState("");
  const [isGroupNameSubmitted, setIsGroupNameSubmitted] = useState(false);
  const [participantName, setParticipantName] = useState("");
  const [newParticipantNames, setNewParticipantNames] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [createdParticipants, setCreatedParticipants] = useState(null);
  const [meId, setMeId] = useState(null);

  const popupRef = useRef(null);
  const sessionRef = useRef(0);

  const { showError, showInfo } = useToast();
  const dispatch = useDispatch();
  const currentGroup = useSelector(selectCurrentGroup);

  const isEditMode = isCurrentGroup && !!currentGroup;
  const existingParticipants = isEditMode
    ? currentGroup.participants || []
    : [];
  const idsInUse = useMemo(
    () => participantIdsInUse(isEditMode ? currentGroup : null),
    [isEditMode, currentGroup],
  );
  const canDeleteExisting = isEditMode && isGroupNameSubmitted;
  const canRename = canDeleteExisting;
  const isDeleting = deletingId !== null;
  const isAssignStep = createdParticipants !== null;
  const isBusy = isLoading || isDeleting;
  const trimmedParticipantName = participantName.trim();
  const isDuplicateParticipant = isDuplicateName(participantName, [
    ...existingParticipants.map((participant) => participant.name),
    ...newParticipantNames,
  ]);
  const backClose = usePopupBackClose(opened, () => !isBusy);
  useEnterKeys(backClose.id, {
    "group-name-input": { hint: "next" },
    "participant-name-input": { hint: "next" },
  });

  // F7's popupOpen fires with the previous render's handlers (stale mode).
  const currentGroupName = currentGroup?.name;
  useEffect(() => {
    if (opened && isEditMode) setName(currentGroupName || "");
  }, [opened, isEditMode, currentGroupName]);

  useEffect(() => {
    if (opened && isEditMode && startAtParticipants)
      setIsGroupNameSubmitted(true);
  }, [opened, isEditMode, startAtParticipants]);

  // F7 also reads this on close to unbind its handler: must be true before closing.
  const setClosable = (closable) => {
    if (popupRef.current) {
      popupRef.current.params.closeByBackdropClick = closable;
    }
  };
  useEffect(() => {
    if (popupRef.current) {
      popupRef.current.params.closeByBackdropClick = !isBusy;
    }
  }, [isBusy]);

  const closePopup = () => {
    setClosable(true);
    popupRef.current?.close();
  };

  const whileOpen = (callback) => {
    const session = sessionRef.current;
    return (...args) => {
      if (session === sessionRef.current) callback(...args);
    };
  };

  const resetState = () => {
    setSavedGroup(null);
    setName("");
    setIsGroupNameSubmitted(false);
    setParticipantName("");
    setNewParticipantNames([]);
    setIsLoading(false);
    setDeletingId(null);
    setCreatedParticipants(null);
    setMeId(null);
  };

  const submitGroupName = () => {
    if (name.trim() === "") {
      showError("Bitte gib einen Gruppennamen ein.");
      return;
    }
    if (!isEditMode) {
      setIsGroupNameSubmitted(true);
      return;
    }
    setIsLoading(true);
    dispatch(
      updateGroup(
        currentGroup.id,
        name,
        whileOpen((group, { queued = false } = {}) => {
          if (queued) showInfo(queuedText("Gruppenname gespeichert"));
          setSavedGroup(group);
          setIsGroupNameSubmitted(true);
          setIsLoading(false);
        }),
        whileOpen((message) => {
          showError(message);
          setIsLoading(false);
        }),
      ),
    );
  };

  const addParticipantsTo = (groupId) =>
    dispatch(
      addParticipants(
        newParticipantNames,
        groupId,
        whileOpen((group) => {
          setIsLoading(false);
          if (isEditMode) {
            closePopup();
            return;
          }
          const participants = group?.participants || [];
          setCreatedParticipants(participants);
          setMeId(participants.length === 1 ? participants[0].id : null);
        }),
        whileOpen((message, { participantsSaved = false } = {}) => {
          showError(message);
          setIsLoading(false);
          // Already stored: a retry would add them twice.
          if (participantsSaved) closePopup();
        }),
      ),
    );

  const confirmRemoveExistingParticipant = (participant) => {
    if (isBusy) return;
    confirmDialog({
      title: "Person entfernen",
      text: `Möchtest du ${participant.name} wirklich aus der Gruppe entfernen?`,
      confirmText: "Entfernen",
      destructive: true,
      cssClass: "delete-participant-dialog",
      onConfirm: () => {
        setDeletingId(participant.id);
        dispatch(
          deleteParticipant(
            participant.id,
            whileOpen((group, { queued = false } = {}) => {
              if (queued) showInfo(queuedText("Person entfernt"));
              setDeletingId(null);
            }),
            whileOpen((message) => {
              setDeletingId(null);
              showError(message);
            }),
          ),
        );
      },
    });
  };

  const openRename = (participant) => {
    if (!isBusy) onRenameParticipant(participant);
  };

  const saveParticipants = () => {
    if (newParticipantNames.length === 0) {
      if (isEditMode) {
        closePopup();
      } else {
        showError("Bitte füge mindestens einen Teilnehmer hinzu.");
      }
      return;
    }
    setIsLoading(true);
    if (savedGroup) {
      addParticipantsTo(savedGroup.id);
      return;
    }
    dispatch(
      createGroup(
        name,
        whileOpen((group) => {
          setSavedGroup(group);
          addParticipantsTo(group.id);
        }),
        whileOpen((message) => {
          showError(message);
          setIsLoading(false);
        }),
      ),
    );
  };

  const assignAndClose = (participantId) => {
    if (isBusy) return;
    setMeId(participantId);
    setIsLoading(true);
    dispatch(
      assignMe(
        savedGroup.id,
        participantId,
        whileOpen(() => {
          vibrate();
          setIsLoading(false);
          closePopup();
        }),
        whileOpen((message) => {
          showError(message);
          setIsLoading(false);
        }),
      ),
    );
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isBusy || isAssignStep) return;
    if (!isGroupNameSubmitted) {
      submitGroupName();
    } else if (isDuplicateParticipant) {
      return;
    } else if (trimmedParticipantName) {
      setNewParticipantNames((prev) => [...prev, trimmedParticipantName]);
      setParticipantName("");
    } else {
      saveParticipants();
    }
  };

  const handlePopupClosed = () => {
    sessionRef.current += 1;
    if (savedGroup) {
      dispatch(getGroupStatus(savedGroup.id));
      dispatch(refreshGroups());
    }
    resetState();
    onClose();
  };

  const step = isAssignStep ? 2 : isGroupNameSubmitted ? 1 : 0;
  const stepCount = isEditMode ? 2 : 3;
  const title = TITLES[isEditMode ? "edit" : "create"][step];
  const submitLabel = !isGroupNameSubmitted
    ? "Weiter"
    : trimmedParticipantName
      ? "Hinzufügen"
      : "Gruppe speichern";

  return (
    <Popup
      {...backClose}
      className="create-group-popup"
      opened={opened}
      onPopupOpened={(popup) => {
        popupRef.current = popup;
      }}
      onPopupClosed={handlePopupClosed}
    >
      {/* is-busy is on <Page>: a changing Popup className makes framework7-react drop modal-in. */}
      <Page className={isBusy ? "is-busy" : ""}>
        <PopupNavbar title={title} />
        <div className="popup-progress">
          {isBusy && <Progressbar infinite />}
        </div>
        <BlockTitle className="margin-top">
          Schritt {step + 1} von {stepCount}
        </BlockTitle>
        {isAssignStep && (
          <AssignStep
            key="assign-step"
            participants={createdParticipants}
            selectedId={meId}
            disabled={isBusy}
            onAssign={assignAndClose}
            onSkip={closePopup}
          />
        )}
        {!isAssignStep && (
          <Block key="form">
            <form onSubmit={handleSubmit}>
              <List strong>
                {!isGroupNameSubmitted ? (
                  <ListInput
                    key="group-name"
                    floatingLabel
                    label="Gruppenname"
                    name="group-name-input"
                    type="text"
                    outline
                    placeholder="Gruppenname"
                    autocomplete="off"
                    autocapitalize="sentences"
                    required
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                  />
                ) : (
                  <ListInput
                    key="participant-name"
                    floatingLabel
                    label="Teilnehmername"
                    name="participant-name-input"
                    type="text"
                    outline
                    placeholder="Teilnehmername"
                    autocomplete="off"
                    autocapitalize="words"
                    value={participantName}
                    onChange={(event) => setParticipantName(event.target.value)}
                    errorMessage={
                      isDuplicateParticipant
                        ? duplicateNameMessage(participantName)
                        : undefined
                    }
                    errorMessageForce={isDuplicateParticipant}
                  />
                )}
                <ListItem>
                  <Button
                    fill
                    type="submit"
                    disabled={isDuplicateParticipant}
                  >
                    {submitLabel}
                  </Button>
                </ListItem>
              </List>
            </form>
          </Block>
        )}
        {!isAssignStep &&
          (existingParticipants.length > 0 ||
            newParticipantNames.length > 0) && (
            <Block>
              <BlockTitle>Teilnehmer</BlockTitle>
              <List strong dividers>
                {existingParticipants.map((participant) => {
                  const inUse = idsInUse.has(String(participant.id));
                  return (
                    <ListItem
                      key={`existing-${participant.id}`}
                      className={
                        canRename
                          ? "existing-participant can-rename"
                          : "existing-participant"
                      }
                      title={participant.name}
                      after={
                        canDeleteExisting && inUse ? "hat Ausgaben" : undefined
                      }
                      onClick={
                        canRename ? () => openRename(participant) : undefined
                      }
                    >
                      {canRename && (
                        <Icon
                          slot="media"
                          className="rename-participant-icon"
                          md="material:edit"
                          ios="f7:pencil"
                        />
                      )}
                      {canDeleteExisting && (
                        <Button
                          slot="inner-end"
                          className="delete-participant-button"
                          disabled={inUse || isBusy}
                          aria-label={`${participant.name} entfernen`}
                          onClick={(event) => {
                            event.stopPropagation();
                            confirmRemoveExistingParticipant(participant);
                          }}
                        >
                          {deletingId === participant.id ? (
                            <Preloader size={20} />
                          ) : (
                            <Icon md="material:delete" ios="f7:trash_fill" />
                          )}
                        </Button>
                      )}
                    </ListItem>
                  );
                })}
                {newParticipantNames.map((participant, index) => (
                  <ListItem key={`new-${index}`} title={participant}>
                    <Button
                      slot="inner-end"
                      aria-label={`${participant} entfernen`}
                      onClick={() =>
                        setNewParticipantNames((prev) =>
                          prev.filter((_, i) => i !== index),
                        )
                      }
                    >
                      <Icon md="material:delete" ios="f7:trash_fill" />
                    </Button>
                  </ListItem>
                ))}
              </List>
            </Block>
          )}
      </Page>
    </Popup>
  );
};

export default CreateGroupPopup;
